import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import ErrorBoundary from "@/components/ErrorBoundary";
import { BottomTabBar, MOBILE_TABBAR_HEIGHT_PX } from "@/components/navigation/BottomTabBar";
import { useIsMobile } from "@/hooks/use-mobile";
import CookieConsent from "@/components/CookieConsent";

// Lazy load all route components for code splitting
const Index = lazy(() => import("./pages/Index"));
const Assistant = lazy(() => import("./pages/Assistant"));
const Category = lazy(() => import("./pages/Category"));
const Fix = lazy(() => import("./pages/Fix"));
const Auth = lazy(() => import("./pages/Auth"));
const Upgrade = lazy(() => import("./pages/Upgrade"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const ToolBox = lazy(() => import("./pages/ToolBox"));
const FeaturedTools = lazy(() => import("./pages/FeaturedTools"));
const ToolReview = lazy(() => import("./pages/ToolReview"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Bookmarks = lazy(() => import("./pages/Bookmarks"));
const FeedbackHistory = lazy(() => import("./pages/FeedbackHistory"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const HomeProfile = lazy(() => import("./pages/HomeProfile"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const AffiliateDisclosure = lazy(() => import("./pages/AffiliateDisclosure"));
const Support = lazy(() => import("./pages/Support"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
    },
  },
});

const AppShell = () => {
  const isMobile = useIsMobile();

  return (
    <div
      className="min-h-screen"
      style={
        isMobile
          ? { paddingBottom: `calc(env(safe-area-inset-bottom) + ${MOBILE_TABBAR_HEIGHT_PX}px)` }
          : undefined
      }
    >
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/assistant" element={<Assistant />} />
        <Route path="/category/:name" element={<Category />} />
        <Route path="/fix/:id" element={<Fix />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/upgrade" element={<Upgrade />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/toolbox" element={<ToolBox />} />
        <Route path="/featured-tools" element={<FeaturedTools />} />
        <Route path="/tool-review/:id" element={<ToolReview />} />
        <Route path="/bookmarks" element={<Bookmarks />} />
        <Route path="/feedback-history" element={<FeedbackHistory />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id" element={<ProjectDetail />} />
        <Route path="/home-profile" element={<HomeProfile />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
        <Route path="/support" element={<Support />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {isMobile && <BottomTabBar />}
    </div>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <AppShell />
            </Suspense>
            <CookieConsent />
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
